import React, { useState, useEffect } from 'react';
import { 
  Users, 
  GraduationCap, 
  Calendar,
  Clock,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  Phone,
  Mail,
  Building,
  TrendingUp,
  FileText,
  DollarSign
} from 'lucide-react';

const AgentAnalyticsDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [subAgents, setSubAgents] = useState([]);
  const [scheduledStudents, setScheduledStudents] = useState([]);
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [scheduleStats, setScheduleStats] = useState(null);
  const [programmeData, setProgrammeData] = useState([]);
  const [applicationAnalytics, setApplicationAnalytics] = useState(null);
  const [engagementMetrics, setEngagementMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Get agent ID from localStorage
  const getAgentId = () => {
    const userData = JSON.parse(localStorage.getItem('user')) || {};
    return userData.id || '123'; // fallback for demo
  };

  const agentId = getAgentId();

  // API base URL - adjust according to your setup
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/sub-analytics/subagent/${agentId}`;

  const fetchData = async () => {
  setLoading(true);
  const token = localStorage.getItem("token");

  try {
    const endpoints = [
      `${API_BASE}/dashboard-summary`,
      `${API_BASE}/scheduled-students?days=7`,
      `${API_BASE}/overdue-students`,
      `${API_BASE}/schedule-stats`,
      `${API_BASE}/students-by-programme`,
      `${API_BASE}/application-analytics`,
      `${API_BASE}/engagement-metrics`,
      `${API_BASE}/programme-performance`
    ];

    const responses = await Promise.all(
      endpoints.map(url =>
        fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        })
          .then(res => res.ok ? res.json() : null)
          .catch(err => {
            console.error(`Error fetching ${url}:`, err);
            return null;
          })
      )
    );

    const [
      dashboardRes,
      scheduledRes,
      overdueRes,
      scheduleStatsRes,
      programmeRes,
      applicationRes,
      engagementRes,
      performanceRes
    ] = responses;

    if (dashboardRes) setDashboardData(dashboardRes);
    if (scheduledRes) setScheduledStudents(scheduledRes.students || []);
    if (overdueRes) setOverdueStudents(overdueRes.all_overdue_students || []);
    if (scheduleStatsRes) setScheduleStats(scheduleStatsRes.stats || scheduleStatsRes);
    if (programmeRes) setProgrammeData(programmeRes.data || []);
    if (applicationRes) setApplicationAnalytics(applicationRes);
    if (engagementRes) setEngagementMetrics(engagementRes);

    setSubAgents([
      {
        id: 1,
        first_name: "John",
        last_name: "Smith",
        email: "john.smith@example.com",
        total_users: 25,
        total_students: 18,
        conversion_rate: 72,
        is_active: true
      },
      {
        id: 2,
        first_name: "Sarah",
        last_name: "Johnson",
        email: "sarah.johnson@example.com",
        total_users: 30,
        total_students: 22,
        conversion_rate: 73,
        is_active: true
      }
    ]);

  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchData();
  }, [agentId]);

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", trend = null }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${
            color === 'blue' ? 'bg-blue-50' : 
            color === 'green' ? 'bg-green-50' : 
            color === 'orange' ? 'bg-orange-50' : 
            color === 'purple' ? 'bg-purple-50' : 
            'bg-red-50'
          }`}>
            <Icon className={`h-6 w-6 ${
              color === 'blue' ? 'text-blue-600' : 
              color === 'green' ? 'text-green-600' : 
              color === 'orange' ? 'text-orange-600' : 
              color === 'purple' ? 'text-purple-600' : 
              'text-red-600'
            }`} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {trend && (
          <div className="flex items-center space-x-1">
            <TrendingUp className={`h-4 w-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const TabButton = ({ id, label, icon: Icon, active, onClick, badge = null }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors relative ${
        active 
          ? 'bg-blue-100 text-blue-700' 
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );

  const StudentCard = ({ student, isOverdue = false }) => (
    <div className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow ${
      isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{student.first_name} {student.last_name}</h4>
          <p className="text-sm text-gray-600">{student.programme || 'No Programme'}</p>
          <p className="text-xs text-gray-500">{student.university || 'No University'}</p>
        </div>
        <div className="text-right">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            student.student_status === 'Active' ? 'bg-green-100 text-green-800' : 
            student.student_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {student.student_status || 'Unassigned'}
          </span>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {student.scheduled_date ? new Date(student.scheduled_date).toLocaleDateString() : 'No scheduled date'}
          </span>
          {isOverdue && student.days_overdue && (
            <span className="text-xs text-red-600 font-medium">
              ({student.days_overdue} days overdue)
            </span>
          )}
          {!isOverdue && student.days_until_scheduled !== undefined && (
            <span className="text-xs text-orange-600 font-medium">
              (in {student.days_until_scheduled} days)
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-4">
          {student.email && (
            <div className="flex items-center space-x-1">
              <Mail className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600 truncate max-w-32">{student.email}</span>
            </div>
          )}
          {student.mobile_no && (
            <div className="flex items-center space-x-1">
              <Phone className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">{student.mobile_no}</span>
            </div>
          )}
        </div>
        
        {student.student_id && (
          <p className="text-xs text-gray-500">
            Student ID: {student.student_id}
          </p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-lg font-medium text-gray-700">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">Monitor your students and performance metrics</p>
            </div>
            <button 
              onClick={fetchData}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-white p-1 rounded-lg shadow-sm border">
          <TabButton 
            id="overview" 
            label="Overview" 
            icon={GraduationCap} 
            active={activeTab === 'overview'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="scheduled" 
            label="Scheduled" 
            icon={Calendar} 
            active={activeTab === 'scheduled'} 
            onClick={setActiveTab}
            badge={scheduledStudents.length > 0 ? scheduledStudents.length : null}
          />
          <TabButton 
            id="overdue" 
            label="Overdue" 
            icon={AlertTriangle} 
            active={activeTab === 'overdue'} 
            onClick={setActiveTab}
            badge={overdueStudents.length > 0 ? overdueStudents.length : null}
          />
          <TabButton 
            id="analytics" 
            label="Analytics" 
            icon={TrendingUp} 
            active={activeTab === 'analytics'} 
            onClick={setActiveTab} 
          />
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={GraduationCap}
                title="Total Students"
                value={dashboardData?.overview?.totalStudents || 0}
                subtitle={`Active: ${dashboardData?.userActivity?.activeUsers || 0}`}
                color="blue"
                trend={dashboardData?.growth?.newStudentsThisMonth || 0}
              />
              <StatCard
                icon={Users}
                title="Total Users"
                value={dashboardData?.overview?.totalUsers || 0}
                subtitle={`${dashboardData?.userActivity?.verificationRate || 0}% verified`}
                color="green"
                trend={dashboardData?.growth?.newUsersThisMonth || 0}
              />
              <StatCard
                icon={Calendar}
                title="Scheduled This Week"
                value={scheduleStats?.upcoming_interviews || 0}
                subtitle={`Today: ${scheduleStats?.today_interviews || 0}`}
                color="orange"
              />
              <StatCard
                icon={AlertTriangle}
                title="Overdue"
                value={scheduleStats?.overdue_interviews || 0}
                subtitle={`Severe: ${scheduleStats?.severely_overdue || 0}`}
                color="red"
              />
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={FileText}
                title="Documents"
                value={dashboardData?.overview?.totalDocuments || 0}
                subtitle="Uploaded files"
                color="green"
              />
              <StatCard
                icon={UserCheck}
                title="Completion Rate"
                value={`${Math.round(
                  ((dashboardData?.applicationProgress?.usersWithPersonalInfo || 0) + 
                   (dashboardData?.applicationProgress?.usersWithCourseDetails || 0) + 
                   (dashboardData?.applicationProgress?.usersWithDocuments || 0)) / 
                  ((dashboardData?.overview?.totalUsers || 1) * 3) * 100
                )}%`}
                subtitle="Profile completion"
                color="orange"
              />
            </div>

            {/* Application Progress Overview */}
            {dashboardData?.applicationProgress && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Progress</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {dashboardData.applicationProgress.completionRates.personalInfo}%
                    </div>
                    <div className="text-sm text-gray-600">Personal Info Complete</div>
                    <div className="text-xs text-gray-500">
                      {dashboardData.applicationProgress.usersWithPersonalInfo} users
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {dashboardData.applicationProgress.completionRates.courseDetails}%
                    </div>
                    <div className="text-sm text-gray-600">Course Details Complete</div>
                    <div className="text-xs text-gray-500">
                      {dashboardData.applicationProgress.usersWithCourseDetails} users
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {dashboardData.applicationProgress.completionRates.documents}%
                    </div>
                    <div className="text-sm text-gray-600">Documents Uploaded</div>
                    <div className="text-xs text-gray-500">
                      {dashboardData.applicationProgress.usersWithDocuments} users
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Views */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Scheduled */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Scheduled (Next 7 Days)</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {scheduledStudents.slice(0, 5).map((student, index) => (
                    <StudentCard key={index} student={student} />
                  ))}
                  {scheduledStudents.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No scheduled appointments in the next 7 days</p>
                    </div>
                  )}
                </div>
                {scheduledStudents.length > 5 && (
                  <button 
                    onClick={() => setActiveTab('scheduled')}
                    className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View all {scheduledStudents.length} scheduled students →
                  </button>
                )}
              </div>

              {/* Overdue */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Overdue Students</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {overdueStudents.slice(0, 5).map((student, index) => (
                    <StudentCard key={index} student={student} isOverdue={true} />
                  ))}
                  {overdueStudents.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <UserCheck className="h-12 w-12 mx-auto mb-4 text-green-300" />
                      <p>No overdue appointments!</p>
                    </div>
                  )}
                </div>
                {overdueStudents.length > 5 && (
                  <button 
                    onClick={() => setActiveTab('overdue')}
                    className="mt-4 text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    View all {overdueStudents.length} overdue students →
                  </button>
                )}
              </div>
            </div>

            {/* Engagement Metrics */}
            {engagementMetrics && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{engagementMetrics.incompleteProfiles}</div>
                    <div className="text-sm text-gray-600">Incomplete Profiles</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{engagementMetrics.usersWithoutDocuments}</div>
                    <div className="text-sm text-gray-600">Users Without Documents</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{engagementMetrics.messaging?.activeMessageUsers || 0}</div>
                    <div className="text-sm text-gray-600">Active Message Users</div>
                    <div className="text-xs text-gray-500">{engagementMetrics.messaging?.totalMessages || 0} total messages</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Scheduled Students (Next 7 Days) - {scheduledStudents.length} total
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scheduledStudents.map((student, index) => (
                  <StudentCard key={index} student={student} />
                ))}
                {scheduledStudents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No scheduled appointments in the next 7 days</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overdue Tab */}
        {activeTab === 'overdue' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Overdue Students - {overdueStudents.length} total
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {overdueStudents.map((student, index) => (
                  <StudentCard key={index} student={student} isOverdue={true} />
                ))}
                {overdueStudents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <UserCheck className="h-12 w-12 mx-auto mb-4 text-green-300" />
                    <p>No overdue appointments!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Application Status Analytics */}
            {applicationAnalytics && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Status Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {applicationAnalytics.statusDistribution?.map((status, index) => (
                    <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{status.count}</div>
                      <div className="text-sm text-gray-600">{status.status}</div>
                      <div className="text-xs text-gray-500">{status.percentage}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Programme Performance */}
            
            {/* Document Submission Analytics */}
            {applicationAnalytics?.documentSubmissions && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Submissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {applicationAnalytics.documentSubmissions.map((doc, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-lg font-semibold text-gray-900">{doc.count}</div>
                      <div className="text-sm text-gray-600">{doc.document_type}</div>
                      <div className="text-xs text-gray-500">{doc.uniqueUsers} unique users</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sub-Agents Tab */}
        
      </div>
    </div>
  );
};

export default AgentAnalyticsDashboard;